import { ConfigProvider, theme as antdTheme } from 'antd';
import React, { createContext, useContext, useEffect, useState, memo, ReactNode } from 'react';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';

import { CustomThemeToken, storedThemeName, themes } from './theme.utils';

export let token: CustomThemeToken = antdTheme.getDesignToken(themes[storedThemeName]);

const ThemeContext = createContext({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  changeTheme: (themeName: string) => {
    // Implementation provided by provider
  },
  name: storedThemeName,
});

interface MemoizedProviderProps {
  theme: unknown;
  token: CustomThemeToken;
  children: ReactNode;
}

const MemoizedProvider = memo(({ theme, token, children }: MemoizedProviderProps) => (
  <ConfigProvider theme={theme}>
    <StyledComponentsThemeProvider theme={{ token }}>{children}</StyledComponentsThemeProvider>
  </ConfigProvider>
));

const ThemeProvider = ({ children }: React.PropsWithChildren) => {
  const [currentTheme, setCurrentTheme] = useState(themes[storedThemeName]);
  const [currentToken, setCurrentToken] = useState(antdTheme.getDesignToken(currentTheme as unknown));

  useEffect(() => {
    token = antdTheme.getDesignToken(currentTheme as unknown);
    setCurrentToken(token);
  }, [currentTheme]);

  const changeTheme = (themeName: string) => {
    const theme = themes[themeName];
    if (theme) {
      setCurrentTheme(theme);
      localStorage.setItem('theme', themeName);
    }
  };

  return (
    <ThemeContext.Provider value={{ changeTheme, name: currentTheme.name }}>
      <MemoizedProvider theme={currentTheme} token={currentToken}>
        {children}
      </MemoizedProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeProvider;
